import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-responsive-modal';
import socket from '../utils/socket';
/** Components */
import { LogoPeque } from '../logo';
/** Enums */
import { EVENT_TYPE } from '../utils/enums';
/** Services */
import { getCurrentUser } from '../utils/services/authService';
import { showConfirmAlert, showErrorAlert, showSuccessAlert } from '../utils/alerts';
/** CSS */
import '../assets/css/modal.css';

const SuspectModal = ({ onModalClosed }) => {
  const [building, setBuilding] = useState(null);
  const [error, setError] = useState(null);
  const errorCleaner = useRef(null);

  useEffect(() => {
    const fetchBuilding = async () => {
      const token = localStorage.getItem('authToken');
      const {
        data: { spaces },
      } = await getCurrentUser(token);
      if (!spaces?.length) return;
      const [{ building }] = spaces;
      setBuilding(building);
    };

    fetchBuilding();

    socket.on('user_event:error', ({ error }) => {
      const message = error.message || JSON.stringify(error);
      console.error(message);
      setError(message);
      errorCleaner.current = setTimeout(() => {
        setError(null);
      }, 5000);
    });

    return () => {
      socket.off('user_event:error');
      if (errorCleaner.current) {
        clearTimeout(errorCleaner.current);
      }
    };
  }, []);

  const handleTriggerEvent = async (event) => {
    try {
      if (!building?.id) {
        showErrorAlert('¡Ups!', 'No pudimos obtener el edificio. Intenta nuevamente.');
        return;
      }

      let errorReceived = false;

      socket.once('user_event:error', () => {
        errorReceived = true;
        showErrorAlert('¡Ups!', 'No pudimos enviar la alerta.');
      });

      socket.emit('user_event', {
        event,
        buildingId: building.id,
      });

      if (event === EVENT_TYPE.INTRUSION || event === EVENT_TYPE.EMERGENCY) {
        setTimeout(() => {
          if (!errorReceived) {
            if (event === EVENT_TYPE.INTRUSION) {
              showSuccessAlert(
                '¡Alerta de Seguridad enviada!',
                'Notificación enviada correctamente.',
                true,
              ).then(() => onModalClosed());
            } else if (event === EVENT_TYPE.EMERGENCY) {
              showSuccessAlert(
                '¡Alerta de Emergencia enviada!',
                'Notificación enviada correctamente.',
                true,
              ).then(() => onModalClosed());
            }
          }
        }, 1000);
      }
    } catch (error) {
      showErrorAlert('¡Ups!', 'Ocurrió un error al realizar la notificación');
    }
  };

  const emitMuteEventForBuilding = async (event, building) => {
    try {
      if (!event || !building?.id) {
        showErrorAlert('¡Ups!', 'Tuvimos un problema con la información del edificio. Intenta de nuevo más tarde.');
        return;
      }

      let errorReceived = false;

      socket.once('user_event:error', () => {
        errorReceived = true;
        showErrorAlert(
          'Lo sentimos',
          'No pudimos silenciar la alarma en este momento. Por favor, vuelve a intentarlo pronto.',
        );
      });

      socket.emit('user_event', {
        event,
        buildingId: building.id,
      });

      setTimeout(() => {
        if (!errorReceived) {
          showSuccessAlert(
            '¡Solicitud enviada!',
            `La solicitud para silenciar la alarma del edificio <b>${building.name}</b> fue enviada correctamente.`,
            true,
          ).then(() => {
            onModalClosed();
          });
        }
      }, 1000);
    } catch (error) {
      showErrorAlert(
        '¡Algo salió mal!',
        'Ocurrió un error al intentar silenciar la alarma del edificio. Por favor, vuelve a intentarlo pronto.',
      );
    }
  };

  const confirmMuteBuilding = () => {
    showConfirmAlert({
      message: `Esta acción silenciará y desactivará la alarma del edificio ${building?.name}.`,
      title: 'Advertencia',
      cancelButton: 'Cancelar',
      continueButton: 'Silenciar edificio',
      onConfirm: () => {
        emitMuteEventForBuilding(EVENT_TYPE.MUTE, {
          id: building?.id,
          name: building?.name,
        });
      },
    });
  };

  const confirmEmergencyBuilding = () => {
    showConfirmAlert({
      message: `Esta acción enviará una alerta de emergencia al edificio ${building?.name}.`,
      title: 'Advertencia',
      cancelButton: 'Cancelar',
      continueButton: 'Enviar alerta',
      onConfirm: () => {
        handleTriggerEvent(EVENT_TYPE.EMERGENCY);
      },
    });
  };

  return (
    <Modal open={true} onClose={onModalClosed} classNames={{ overlay: 'background-modal' }} center>
      <div className="alerta" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <LogoPeque />
            </h5>
            <button type="button" className="close" onClick={onModalClosed}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <h2>Eventos</h2>
            <p>Usa las siguientes opciones para reportar:</p>
          </div>

          <div className="modal-footer events">
            <button
              type="button"
              className="btn btn-event btn-security"
              onClick={() => handleTriggerEvent(EVENT_TYPE.INTRUSION)}
            >
              <span className="btn-event-content">
                Alerta de Seguridad:
                <br />
                Para intrusos o accesos no autorizados.
              </span>
            </button>
            <button
              type="button"
              className="btn btn-event btn-emergency"
              onClick={confirmEmergencyBuilding}
            >
              <span className="btn-event-content">
                Alerta de Emergencia:
                <br />
                Terremotos, incendios, y otras situaciones críticas.
              </span>
            </button>
            <button
              type="button"
              className="btn btn-event btn-mute"
              onClick={confirmMuteBuilding}>
              <span className="btn-event-content">
                Silenciar alarma:
                <br />
                Solo si ya revisaste que todo está bien.
              </span>
            </button>
            <button
              type="button"
              className="btn btn-event btn-medical-emergency"
              onClick={() => handleTriggerEvent(EVENT_TYPE.MEDICAL_EMERGENCY)}
            >
              Emergencia médica:
              <br />
              Si necesitas ayuda médica, avisaremos a tu familia.
            </button>
            <button
              type="button"
              className="btn btn-event btn-technical-support"
              onClick={() => handleTriggerEvent(EVENT_TYPE.TECHNICAL_SUPPORT)}
            >
              Soporte técnico:
              <br />
              ¿Algún problema técnico? Con gusto te asistimos.
            </button>
          </div>

          {error && (
            <div className="alert alert-danger p-2" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

SuspectModal.propTypes = {
  onModalClosed: PropTypes.func,
};

export default SuspectModal;
